import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Panel } from "@/components/Panel";
import { Brain, Zap, AlertTriangle, TrendingUp, Filter, Database } from "lucide-react";
import UsageUpsellBanner from "@/components/UsageUpsellBanner";

const TYPES = ["ALL", "TREND", "ANOMALY", "PREDICTION"];
const TYPE_STYLE = {
  TREND: { cls: "text-[#FFB000] border-[#FFB000]/30", icon: TrendingUp },
  ANOMALY: { cls: "text-[#FF3B30] border-[#FF3B30]/30", icon: AlertTriangle },
  PREDICTION: { cls: "text-[#00E676] border-[#00E676]/30", icon: Zap },
};
const IMPACT_COLOR = { HIGH: "text-[#FF3B30]", MEDIUM: "text-[#FFB000]", LOW: "text-zinc-400" };
const SYMS = { USD:"$", EUR:"€", GBP:"£", NGN:"₦", ZAR:"R", KES:"KSh", GHS:"₵", EGP:"£", INR:"₹", BRL:"R$", MXN:"$", AED:"د.إ", SAR:"﷼", JPY:"¥", CNY:"¥", AUD:"A$", CAD:"C$", SGD:"S$" };
const fmt = (n, code) => {
  const s = SYMS[code] || (code ? `${code} ` : "$");
  const abs = Math.abs(n || 0);
  if (abs >= 1e6) return `${s}${(n/1e6).toFixed(1)}M`;
  return `${s}${(n/1e3).toFixed(0)}K`;
};

export default function Insights() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("ALL");
  const [hasReal, setHasReal] = useState(null);

  const load = () => api.get("/insights").then(r => setList(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/dashboard/metrics").then(r => setHasReal(!!r.data?.has_real_sources)).catch(() => setHasReal(false));
  }, []);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/insights/generate");
      toast.success(`Generated ${data.length} insights via DARA.`);
      load();
    } catch {
      toast.error("Generation failed");
    }
    setLoading(false);
  };

  const filtered = list.filter(i => filter === "ALL" || i.type === filter);

  return (
    <div className="p-6 space-y-6" data-testid="insights-page">
      <UsageUpsellBanner />
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">MODULE · DARA INSIGHT ENGINE</div>
          <h1 className="font-heading font-black text-4xl tracking-tighter uppercase mt-1">DARA Insights</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-xl">DARA analysis (powered by DARA Engine) continuously scans your data graph and surfaces revenue-ranked trends, anomalies, predictions.</p>
        </div>
        <button
          onClick={generate}
          disabled={loading}
          data-testid="generate-insights-btn"
          className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-3 hover:bg-[#E59E00] transition-colors flex items-center gap-2 disabled:opacity-50"
        >
          <Brain className={`w-4 h-4 ${loading ? "animate-pulse" : ""}`} />
          {loading ? "SCANNING…" : "GENERATE INSIGHTS"}
        </button>
      </div>

      <div className="flex items-center gap-2 border-b border-zinc-900 pb-2">
        <Filter className="w-3 h-3 text-zinc-500" />
        {TYPES.map(t => (
          <button
            key={t}
            onClick={() => setFilter(t)}
            data-testid={`filter-${t.toLowerCase()}`}
            className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 transition-colors ${filter === t ? "bg-[#FFB000] text-black" : "text-zinc-500 hover:text-white"}`}
          >{t}</button>
        ))}
        <span className="font-mono text-[10px] text-zinc-600 ml-auto">{filtered.length} ITEMS</span>
      </div>

      {hasReal === false ? (
        <Panel testId="insights-need-source" className="p-12 text-center">
          <Database className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
          <h3 className="font-heading font-bold text-xl mb-2">Connect real data first.</h3>
          <p className="text-sm text-zinc-500 mb-6 max-w-lg mx-auto">
            DARA Insights run on the columns it actually sees. Upload a CSV (or connect Snowflake / Salesforce) so each insight is anchored in your numbers — not the demo dataset.
          </p>
          <Link to="/app/sources" data-testid="insights-add-source-cta" style={{ backgroundColor: "var(--brand-accent, #FFB000)", color: "var(--brand-accent-text, #000)" }} className="inline-flex items-center gap-2 font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-3 hover:opacity-90 transition-opacity">
            <Database className="w-3.5 h-3.5" /> ADD A DATA SOURCE
          </Link>
        </Panel>
      ) : filtered.length === 0 ? (
        <Panel testId="insights-empty" className="p-12 text-center">
          <Brain className="w-10 h-10 text-zinc-800 mx-auto mb-4" />
          <h3 className="font-heading font-bold text-xl mb-2">The engine is idle.</h3>
          <p className="text-sm text-zinc-500 mb-6">Trigger a DARA scan across your data graph to surface revenue signals.</p>
          <button onClick={generate} disabled={loading} className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-3 hover:bg-[#E59E00]">
            {loading ? "SCANNING…" : "Run first scan"}
          </button>
        </Panel>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map(i => {
            const s = TYPE_STYLE[i.type] || TYPE_STYLE.TREND;
            const Icon = s.icon;
            return (
              <div key={i.id} className="panel p-5 hover:bg-zinc-900/20 transition-colors" data-testid="insight-card">
                <div className="flex items-center justify-between mb-3">
                  <span className={`flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest px-2 py-1 border ${s.cls}`}>
                    <Icon className="w-3 h-3" /> {i.type}
                  </span>
                  <div className="flex items-center gap-3">
                    <span className={`font-mono text-[10px] uppercase tracking-widest ${IMPACT_COLOR[i.impact]}`}>{i.impact} IMPACT</span>
                    <span className="font-mono text-[10px] text-zinc-500">{i.confidence}% CONF</span>
                  </div>
                </div>
                <h3 className="font-heading font-bold text-lg mb-2 leading-tight">{i.title}</h3>
                <p className="text-sm text-zinc-400 mb-4 leading-relaxed">{i.description}</p>
                <div className="flex items-center justify-between border-t border-zinc-900 pt-3">
                  <div className="flex gap-1 flex-wrap">
                    {i.source_types.map(st => (
                      <span key={st} className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-zinc-900 text-zinc-400">{st}</span>
                    ))}
                  </div>
                  <div className="font-mono text-sm text-[#00E676]">+{fmt(i.estimated_value, i.estimated_value_currency)}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
